import React, { useCallback, useState } from "react";

import emailValidator from "email-validator";
import { find, findIndex, isEmpty, remove } from "lodash/fp";

import * as Events from "@app/analytics/Calendar/events";
import useUpcomingEventAccess from "@app/analytics/Calendar/hooks/useUpcomingEventAccess";
import type { UpcomingEventShareDialogTab } from "@app/analytics/Calendar/types";
import EditRecurringMeetingModal, {
  EditRecurringMeetingModalProps,
} from "@app/analytics/Calendar/UpcomingEvents/EditRecurringMeetingModal";
import * as UpcomingEventUtils from "@app/analytics/Calendar/UpcomingEvents/utils";
import useEventGroups from "@app/analytics/hooks/calendar/useEventGroups";
import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import type {
  UserEventGroupPatch,
  UserEventGroup,
} from "@app/analytics/types/eventGroupTypes";
import { AccessLevelChange } from "@app/common/access/ShareReport/AccessList";
import useEditSharing from "@app/common/access/ShareReport/hooks/useEditSharing";
import {
  CloseButton,
  Container,
  Dialog,
  Footer,
  Header,
  SharingButton,
} from "@app/common/access/ShareReport/styles";
import { toast } from "@app/common/hooks/useToasts";
import {
  ApiEntityAccessPatchBody,
  EntityAccessDeleteRequest,
  EventGroupACLResponse,
  ShareEntity,
} from "@app/common/types";
import { replaceWhere } from "@app/utils/arrays";

import Stack from "@rui/Stack";

import AccessAndDistributionTab from "./AccessAndDistributionTab";
import ShareReportTab from "./ShareReportTab";
import {
  DialogTab,
  DialogTabs,
  DialogTabsContainer,
  TabContentContainer,
  ModalTitle,
} from "./styles";

interface UpcomingEventShareDialogProps {
  event: UserEventGroup;
  initialTab?: UpcomingEventShareDialogTab;
  open: boolean;
  onClose: () => void;
}

const TABS = [
  {
    value: "sharing",
    label: "Share Report",
  },
  {
    value: "distribution",
    label: "Access & Distribution",
  },
];

const UpcomingEventShareDialog = ({
  event,
  initialTab = "sharing",
  open,
  onClose,
}: UpcomingEventShareDialogProps) => {
  const [pendingAccessList, setPendingAccessList] = useState<
    EventGroupACLResponse | undefined
  >();
  const [pendingAccessMutations, setPendingAccessMutations] = useState<
    ApiEntityAccessPatchBody[]
  >([]);
  const [pendingRevokeMutations, setPendingRevokeMutations] = useState<
    EntityAccessDeleteRequest[]
  >([]);

  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [selectedTab, setSelectedTab] =
    useState<UpcomingEventShareDialogTab>(initialTab);
  const [
    pendingAccessAndDistributionUpdates,
    setPendingAccessAndDistributionUpdates,
  ] = useState<UserEventGroupPatch>({});

  const editSharing = useEditSharing();
  const { accessList, bulkModifyAccess, grantAccess } = useUpcomingEventAccess(
    event.id,
    { enabled: open, refetchOnMount: true }
  );
  const effectiveAccessList = pendingAccessList ?? accessList;

  const { updateEvent } = useEventGroups();
  const { userSettings } = useUserSettings();

  const canApplyToSeries = Boolean(event.recurringEventId);

  const handleTabChange = (e: React.SyntheticEvent, value: string) => {
    setSelectedTab(value as UpcomingEventShareDialogTab);
  };

  const clearDialogState = () => {
    editSharing.clearSelectedEntities();
    setPendingAccessAndDistributionUpdates({});
    setIsSaving(false);
  };

  const handleClose = () => {
    onClose();
    clearDialogState();
  };

  const handleSaveSuccess = () => {
    toast.success("Changes saved!");
  };

  const handleSave = () => {
    // skip save if there's nothing to update, but still show the UI feedback
    if (
      isEmpty(pendingAccessAndDistributionUpdates) &&
      isEmpty(editSharing.selectedEntities) &&
      isEmpty(pendingAccessMutations) &&
      isEmpty(pendingRevokeMutations)
    ) {
      handleSaveSuccess();
      handleClose();
      return;
    }

    if (canApplyToSeries) {
      // confirm first!
      setShowSaveConfirm(true);
    } else {
      executeSave(false);
    }
  };

  const handleSaveConfirm: EditRecurringMeetingModalProps["onConfirm"] = (
    selected
  ) => {
    executeSave(selected === "allMeetingsInSeries");
  };

  const executeSave = async (applyToAll = false) => {
    const candidates = remove<ShareEntity>(
      (entity) =>
        typeof entity === "string" && !emailValidator.validate(entity),
      editSharing.selectedEntities
    );

    if (candidates.length !== editSharing.selectedEntities.length) {
      if (selectedTab !== "sharing") setSelectedTab("sharing");
      toast.error("Please enter valid email addresses");

      return;
    }

    const entities = candidates.map((c) => ({
      entityId: typeof c === "string" ? c : c.id,
      kind: typeof c === "string" ? "email" : c.type,
      name: typeof c === "string" ? c : c.name,
      accessLevel: editSharing.bulkAccess,
      notifyUser: false,
    }));

    const eventPatchBody: UserEventGroupPatch = isEmpty(
      pendingAccessAndDistributionUpdates
    )
      ? {}
      : {
          ...pendingAccessAndDistributionUpdates,
          applyToRecurringEvent: applyToAll,
        };

    Events.addEntities({ eventId: event.id, numEntities: entities.length });

    if (!isEmpty(pendingAccessAndDistributionUpdates)) {
      Events.changeDistribution({ eventId: event.id, ...eventPatchBody });
    }

    try {
      setIsSaving(true);
      // try to do these updates at the same time, and throw an error if either of them fail.
      // both these mutations handle returning early on empty arguments, so they will be noop
      // updates if only one of these updates actually needs to be made
      const result = await Promise.all([
        grantAccess.mutateAsync({
          entityAccess: entities,
          applyToRecurringEvent: applyToAll,
        }),
        bulkModifyAccess.mutateAsync({
          modifyAccessOperations: pendingAccessMutations,
          revokeAccessOperations: pendingRevokeMutations,
          applyToRecurringEvent: applyToAll,
        }),
        updateEvent.mutateAsync({ id: event.id, body: eventPatchBody }),
      ]);

      if (!result[1].success) {
        toast.error(
          "Some access changes could not be saved. Please try again or contact support if the problem persists."
        );
      }

      handleSaveSuccess();

      handleClose();
    } catch (error) {
      toast.error(
        "Something went wrong when saving your changes. Please try again or contact support if the problem persists."
      );
      return;
    } finally {
      clearDialogState();
    }
  };

  const handleAddPendingUpdate = (update: UserEventGroupPatch) => {
    setPendingAccessAndDistributionUpdates((updates) => ({
      ...updates,
      ...update,
    }));
  };

  const handleDiscard = () => {
    handleClose();
  };

  const title = `Report Sharing: ${event.summary}`;

  const internalParticipantsRole =
    UpcomingEventUtils.internalParticipantAccessLevel({
      event,
      pendingUpdates: pendingAccessAndDistributionUpdates,
      userSettings,
    });

  const externalParticipantsRole =
    UpcomingEventUtils.externalParticipantAccessLevel({
      event,
      pendingUpdates: pendingAccessAndDistributionUpdates,
      userSettings,
    });

  const updatePendingAccessOperations = (
    operation: ApiEntityAccessPatchBody
  ) => {
    setPendingAccessMutations((prev) => {
      const existing = find({ entityId: operation.entityId }, prev);

      if (existing) {
        return replaceWhere(
          prev,
          { ...existing, ...operation },
          (e) => e.entityId === operation.entityId
        );
      }

      return [...prev, operation];
    });
  };

  const updateRevokeAccessOperations = (
    operation: EntityAccessDeleteRequest
  ) => {
    setPendingRevokeMutations((prev) => [...prev, operation]);
  };

  const handleModifyAccess = useCallback(
    (accessLevelChange: AccessLevelChange) => {
      if (!effectiveAccessList) return;

      const targetEntity = find(
        { entityId: accessLevelChange.entityId },
        effectiveAccessList.entityAccess
      );

      if (!targetEntity) return;

      const accessOperation: ApiEntityAccessPatchBody = {
        name: targetEntity.name,
        kind: targetEntity.kind,
        entityId: accessLevelChange.entityId,
        accessLevel: accessLevelChange.accessLevel,
        notifyUser: false,
      };

      const existingEntityAccess = targetEntity;
      if (!existingEntityAccess) return;

      const newEntityAccess = {
        ...existingEntityAccess,
        ...accessOperation,
      };

      const newAccessList: EventGroupACLResponse = {
        ...effectiveAccessList,
        entityAccess: replaceWhere(
          effectiveAccessList.entityAccess,
          newEntityAccess,
          (e) => e.entityId === accessOperation.entityId
        ),
      };

      updatePendingAccessOperations(accessOperation);
      setPendingAccessList(newAccessList);
    },
    [effectiveAccessList]
  );

  const handleRevokeAccess = useCallback(
    (entityId: string) => {
      if (!effectiveAccessList) return;

      const targetEntityIndex = findIndex(
        { entityId },
        effectiveAccessList.entityAccess
      );
      const targetEntity = effectiveAccessList.entityAccess[targetEntityIndex];

      if (!targetEntity) return;

      const operation: EntityAccessDeleteRequest = {
        entityId,
        kind: targetEntity.kind,
      };

      updateRevokeAccessOperations(operation);
      const newAccessList: EventGroupACLResponse = {
        ...effectiveAccessList,
        entityAccess: remove({ entityId }, effectiveAccessList.entityAccess),
      };
      setPendingAccessList(newAccessList);
    },
    [effectiveAccessList]
  );

  return (
    <>
      <Dialog open={open} onClose={handleClose}>
        <Container>
          <Header spacing={1}>
            <ModalTitle title={title} paddingTop={0.5}>
              {title}
            </ModalTitle>
            <CloseButton onClick={handleClose} />
          </Header>
          <DialogTabsContainer>
            <DialogTabs value={selectedTab} onChange={handleTabChange}>
              {TABS.map((tab) => (
                <DialogTab
                  key={tab.value}
                  value={tab.value}
                  label={tab.label}
                />
              ))}
            </DialogTabs>
          </DialogTabsContainer>
          <TabContentContainer>
            {selectedTab === "sharing" ? (
              <ShareReportTab
                accessList={effectiveAccessList}
                event={event}
                externalParticipantsRole={externalParticipantsRole}
                internalParticipantsRole={internalParticipantsRole}
                onGoToTab={setSelectedTab}
                open={open}
                onModifyAccess={handleModifyAccess}
                onRevokeAccess={handleRevokeAccess}
                {...editSharing}
              />
            ) : (
              <AccessAndDistributionTab
                event={event}
                externalParticipantsRole={externalParticipantsRole}
                internalParticipantsRole={internalParticipantsRole}
                pendingUpdates={pendingAccessAndDistributionUpdates}
                onChangeSetting={handleAddPendingUpdate}
              />
            )}
          </TabContentContainer>
          <Footer>
            <Stack direction="row" variant="right" spacing={1}>
              <SharingButton variant="text" onClick={handleDiscard}>
                Discard
              </SharingButton>
              <SharingButton loading={isSaving} onClick={handleSave}>
                Save
              </SharingButton>
            </Stack>
          </Footer>
        </Container>
      </Dialog>
      <EditRecurringMeetingModal
        onConfirm={handleSaveConfirm}
        open={showSaveConfirm}
        onClose={() => setShowSaveConfirm(false)}
        loading={isSaving}
      />
    </>
  );
};

export default UpcomingEventShareDialog;
